import { BusinessStatusMetric, BusinessStatusPeriod } from '../../types/audit';
import { businessDelta, DEFAULT_BUSINESS_STATUS_PERIOD, DeltaKind, formatCount, PERIOD_TEXT } from './businessStatusFormat';
import RingChart, { RingMode } from './RingChart';
import Sparkline from './Sparkline';

interface BusinessStatusTileProps {
  id: string;
  label: string;
  metric: BusinessStatusMetric;
  ringMode: RingMode;
  /** "New garages per month". */
  sparkCaption: string;
  /** #1331 — the window the new/previous counts cover; 30 days by default. */
  period?: BusinessStatusPeriod;
}

const ARROW: Record<DeltaKind, JSX.Element> = {
  up: <svg viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M5 1 9.5 9h-9z" fill="currentColor" /></svg>,
  down: <svg viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M5 9 .5 1h9z" fill="currentColor" /></svg>,
  flat: <svg viewBox="0 0 10 10" aria-hidden="true" focusable="false"><rect x="1" y="4" width="8" height="2" rx="1" fill="currentColor" /></svg>,
};

/** The ring's accessible name, e.g. "Garages live: 27 total, 4 new in last 30 days". */
export function ringAriaLabel(label: string, metric: BusinessStatusMetric, mode: RingMode,
  period: BusinessStatusPeriod = DEFAULT_BUSINESS_STATUS_PERIOD): string {
  const text = PERIOD_TEXT[period];
  if (mode === 'period') {
    return `${label}: ${formatCount(metric.newLast30d)} in ${text.last}, ${formatCount(metric.previous30d)} in ${text.previous}`;
  }
  return `${label}: ${formatCount(metric.total)} total, ${formatCount(metric.newLast30d)} new in ${text.last}`;
}

/**
 * Spec 070 AC-2 — one display-only tile (not a link): label, ring, "+N new" line, delta badge vs the
 * previous period (30 days unless #1331's selector says otherwise), and the 6-month sparkline.
 */
export default function BusinessStatusTile({
  id, label, metric, ringMode, sparkCaption, period = DEFAULT_BUSINESS_STATUS_PERIOD,
}: BusinessStatusTileProps) {
  const d = businessDelta(metric.newLast30d, metric.previous30d, period);
  const text = PERIOD_TEXT[period];
  const labelId = `bs-${id}-label`;
  const isPeriod = ringMode === 'period';

  return (
    <article className="bs-tile" data-tile={id} aria-labelledby={labelId}>
      <h4 className="bs-tile__label" id={labelId}>{label}</h4>
      <div className="bs-tile__main">
        <RingChart
          mode={ringMode}
          highlight={metric.newLast30d}
          base={isPeriod ? metric.previous30d : metric.total}
          ariaLabel={ringAriaLabel(label, metric, ringMode, period)}
          periodUnit={text.span}
        />
        <div className="bs-facts">
          <p className="bs-new">
            <span className="bs-new__key" aria-hidden="true" />
            <span>
              {isPeriod
                ? <><strong data-testid={`bs-new-${id}`}>{formatCount(metric.newLast30d)}</strong> placed in {text.last}</>
                : <><strong data-testid={`bs-new-${id}`}>{metric.newLast30d > 0 ? '+' : ''}{formatCount(metric.newLast30d)}</strong> new in {text.last}</>}
            </span>
          </p>
          <p className="bs-delta">
            <span className={`bs-badge bs-badge--${d.kind}`} data-testid={`bs-delta-${id}`}>
              {ARROW[d.kind]}
              <span className="sr-only">{d.sr}</span>
              <span aria-hidden="true">{d.text}</span>
            </span>
            {d.comparison && <span>{d.comparison}</span>}
          </p>
        </div>
      </div>
      <Sparkline monthly={metric.monthly ?? []} caption={sparkCaption} />
    </article>
  );
}
